/**
 * Pricing calculation utilities
 */
import { PricingTier } from '../types/enums.js';
/**
 * Pricing tier distance boundaries (in miles)
 */
export const PRICING_TIER_BOUNDARIES = {
    STANDARD_MAX: 30,
    TIER_1_MAX: 40,
    TIER_2_MAX: 50,
    DUAL_OPTION_MAX: 50, // Effectively unused — 50+ is inquiry only
};
/**
 * Travel surcharges by tier (in cents)
 */
export const TRAVEL_SURCHARGES = {
    [PricingTier.STANDARD]: 0,
    [PricingTier.TIER_1]: 2500, // $25
    [PricingTier.TIER_2]: 5000, // $50
    [PricingTier.DUAL_OPTION]: 0, // Inquiry only — no surcharge
    [PricingTier.HOSTED]: 0, // Hosted = studio travels, no surcharge
};
/**
 * Marketing materials add-on price (in cents)
 */
export const MARKETING_MATERIALS_PRICE_CENTS = 5000; // $50
/**
 * Deposit amount per class (in cents)
 */
export const DEPOSIT_PER_CLASS_CENTS = 10000; // $100
/**
 * Determine pricing tier based on distance
 */
export function getPricingTier(distanceMiles) {
    if (distanceMiles <= PRICING_TIER_BOUNDARIES.STANDARD_MAX) {
        return PricingTier.STANDARD;
    }
    if (distanceMiles <= PRICING_TIER_BOUNDARIES.TIER_1_MAX) {
        return PricingTier.TIER_1;
    }
    if (distanceMiles <= PRICING_TIER_BOUNDARIES.TIER_2_MAX) {
        return PricingTier.TIER_2;
    }
    if (distanceMiles <= PRICING_TIER_BOUNDARIES.DUAL_OPTION_MAX) {
        return PricingTier.DUAL_OPTION;
    }
    return PricingTier.HOSTED;
}
/**
 * Get travel surcharge for a pricing tier
 */
export function getTravelSurcharge(tier) {
    return TRAVEL_SURCHARGES[tier];
}
/**
 * Calculate available booking paths based on distance
 */
export function getAvailablePaths(distanceMiles) {
    const tier = getPricingTier(distanceMiles);
    switch (tier) {
        case PricingTier.STANDARD:
        case PricingTier.TIER_1:
        case PricingTier.TIER_2:
            return ['direct_booking'];
        case PricingTier.DUAL_OPTION:
        case PricingTier.HOSTED:
            // 50+ miles = inquiry only (no direct booking)
            return ['remote_inquiry'];
        default:
            return ['direct_booking'];
    }
}
/**
 * Calculate total price for a class
 */
export function calculateClassTotal(basePriceCents, distanceMiles, includeMarketing = false) {
    const tier = getPricingTier(distanceMiles);
    const travelSurcharge = getTravelSurcharge(tier);
    const marketingFee = includeMarketing ? MARKETING_MATERIALS_PRICE_CENTS : 0;
    return {
        base_price_cents: basePriceCents,
        travel_surcharge_cents: travelSurcharge,
        marketing_fee_cents: marketingFee,
        total_cents: basePriceCents + travelSurcharge + marketingFee,
        pricing_tier: tier,
    };
}
/**
 * Calculate booking totals for multiple classes
 */
export function calculateBookingTotal(classes, includeMarketing = false) {
    let subtotal = 0;
    let travelSurcharge = 0;
    for (const cls of classes) {
        subtotal += cls.base_price_cents;
        travelSurcharge += getTravelSurcharge(getPricingTier(cls.distance_miles));
    }
    const marketingFee = includeMarketing ? MARKETING_MATERIALS_PRICE_CENTS : 0;
    const total = subtotal + travelSurcharge + marketingFee;
    const deposit = classes.length * DEPOSIT_PER_CLASS_CENTS;
    const balance = total - deposit;
    return {
        subtotal_cents: subtotal,
        travel_surcharge_cents: travelSurcharge,
        marketing_fee_cents: marketingFee,
        total_cents: total,
        deposit_cents: deposit,
        balance_cents: balance,
    };
}
/**
 * Calculate discount amount in cents
 * Returns the discount to subtract (always >= 0, capped at applicable amount)
 */
export function calculateDiscount(input) {
    const { discountType, discountValue, appliesTo, subtotalCents, travelSurchargeCents, marketingFeeCents } = input;
    // Determine the applicable amount based on what the coupon targets
    let applicableAmount;
    switch (appliesTo) {
        case 'sessions_only':
            applicableAmount = subtotalCents + travelSurchargeCents;
            break;
        case 'addons_only':
            applicableAmount = marketingFeeCents;
            break;
        case 'total':
        default:
            applicableAmount = subtotalCents + travelSurchargeCents + marketingFeeCents;
            break;
    }
    if (applicableAmount <= 0)
        return 0;
    let discount;
    if (discountType === 'fixed') {
        discount = discountValue;
    }
    else {
        // Percentage: basis points (1000 = 10%)
        discount = Math.round((applicableAmount * discountValue) / 10000);
    }
    // Cap at applicable amount (no negative totals)
    return Math.min(discount, applicableAmount);
}
/**
 * Format cents as currency string
 */
export function formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(cents / 100);
}
//# sourceMappingURL=pricing.js.map